import React from "react"
import { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { AlertTriangle, Activity } from "lucide-react"
import { toast } from "react-toastify"
import { AxiosResponse } from "axios";
import { useGetFindingsByStudyInstanceUid } from "../../../helpers/api/findings/findings";
import { useUpdateStudyReportWithFindings } from '../../../helpers/api/reports/reports';
import { FindingCard } from "./components/FindingCard";
import { FindingsFilters } from "./components/FindingsFilters";
import { FindingsSummary } from "./components/FindingsSummary";
import { FindingsToolbar } from "./components/FindingsToolbar";

import {
  REPORT_EDIT_CONTROLS,
  buildReportFindingsPayload,
  processFindingsData,
  getCurrentSeriesFindings,
  getReportFindingsFromStudyReport,
  getSelectedFindingDetails,
  filterFindings,
  getUniqueCategories,
  isFindingInReport,
  resolveFindingDbId,
  type Finding,
} from "./utils/utils";
import CollaborationDiscussButton from '../Collaboration/CollaborationDiscussButton';
import { buildFindingTarget } from '../Collaboration/collaboration-types';

const Findings = ({ studyInstanceUid }: { studyInstanceUid: string }) => {
  const { t } = useTranslation("Findings")
  const { t: tReport } = useTranslation("Report")
  const [selectedFindings, setSelectedFindings] = useState<string[]>([])
  const [showReportMode, setShowReportMode] = useState(false)
  const [reportEdits, setReportEdits] = useState<Record<string, string>>({})
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [showFilters, setShowFilters] = useState(false)
  const [activeSeries, setActiveSeries] = useState<string>("")
  const [findingSeriesIndex, setFindingSeriesIndex] = useState<Record<string, number>>({})

  const {
    data: apiData,
    error: errorFindings,
    isLoading: isLoadingFindings,
    refetch: refetchFindings,
  } = useGetFindingsByStudyInstanceUid(studyInstanceUid);
  console.log("findings", apiData)
  const onSuccess = (
    response: AxiosResponse,
    data: Record<string, unknown>
  ) => {
    if (response) {
      console.log("response", response)
    }
  };
  const onError = (response: AxiosResponse, error: Error) => {
    // eslint-disable-next-line no-console
    console.error('Failed to update report:', error);
  };

  const {
    mutateAsync: updateReportWithFindings,
    isPending: isUpdatingReport,
    isError: isUpdateReportError,
  } = useUpdateStudyReportWithFindings(onSuccess, onError);

  const { findingsBySeries, findingsByType, series } = useMemo(() => {
    return processFindingsData(apiData);
  }, [apiData])

  const visibleSeries = useMemo(() => {
    const blindSeries = series.filter((serie) => serie.type === "BLIND")
    if (blindSeries.length > 0) {
      return blindSeries
    }

    const obSeries = series.filter((serie) => serie.type === "OB")
    if (obSeries.length > 0) {
      return obSeries
    }

    return series
  }, [series])

  const studyReport = useMemo(() => {
    return apiData?.study_report || null;
  }, [apiData])

  useMemo(() => {
    if (activeSeries && activeSeries !== "Report" && activeSeries !== "All") {
      const activeSeriesIsVisible = visibleSeries.some((serie) => serie.id === activeSeries)
      if (!activeSeriesIsVisible && visibleSeries.length > 0) {
        setActiveSeries(visibleSeries[0].id)
      }
      return
    }

    if (visibleSeries.length > 0 && !activeSeries) {
      const reportFindingsFromStudy = getReportFindingsFromStudyReport(studyReport);
      if (reportFindingsFromStudy.length > 0) {
        setActiveSeries("Report");
        setSelectedFindings(reportFindingsFromStudy.map(f => f.id));
      } else {
        if (visibleSeries.length === 1) {
          setActiveSeries(visibleSeries[0].id);
        } else {
          const seriesWithMostFindings = visibleSeries.reduce((max, current) =>
            current.availableCount > max.availableCount ? current : max
          );
          setActiveSeries(seriesWithMostFindings.id);
        }
      }
    }
  }, [visibleSeries, activeSeries, studyReport])

  const currentSeriesFindings = useMemo(() => {
    if (activeSeries === "Report") {
      // Report tab: show study_report only (one row per field), no looping through findings/series
      const reportFindings = getReportFindingsFromStudyReport(
        studyReport,
        apiData?.findings ?? []
      );

      // EDD: add if in study_report (getReportFindingsFromStudyReport already includes pred_edd when hasValue)
      // EFW: override result with study_report value for display
      return reportFindings.map((finding) => {
        if (finding.fieldKey === "estimated_fetal_weight" && studyReport?.efw_g != null) {
          return {
            ...finding,
            result: `${Math.round(studyReport.efw_g)}g`,
            description: "Estimated fetal weight calculated from biometric measurements and stored in the report.",
            isReportSpecific: true,
          };
        }
        if (finding.fieldKey === "pred_edd" && studyReport?.pred_edd != null) {
          const currentDate = new Date();
          const remainingWeeks = studyReport.pred_edd;
          const dueDateTimestamp = currentDate.getTime() + (remainingWeeks * 7 * 24 * 60 * 60 * 1000);
          const dueDate = new Date(dueDateTimestamp);
          return {
            ...finding,
            result: dueDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
          };
        }
        return finding;
      });
    }
    return getCurrentSeriesFindings(activeSeries, findingsBySeries, findingsByType, findingSeriesIndex);
  }, [activeSeries, findingsBySeries, findingsByType, findingSeriesIndex, studyReport, apiData?.findings])

  const categories = useMemo(() => {
    return getUniqueCategories(currentSeriesFindings);
  }, [currentSeriesFindings])

  const filteredFindings = useMemo(() => {
    return filterFindings(currentSeriesFindings, selectedCategories);
  }, [selectedCategories, currentSeriesFindings])
  const reportEditCount = Object.keys(reportEdits).length
  const canUpdateActiveSeries = activeSeries !== "Report" && activeSeries !== "All"

  const handleFindingToggle = (findingId: string) => {
    const toggledFinding = currentSeriesFindings.find(
      (finding) => finding.id === findingId
    )
    const toggledEditControl = toggledFinding
      ? REPORT_EDIT_CONTROLS[toggledFinding.fieldKey]
      : undefined

    if (selectedFindings.includes(findingId) && toggledEditControl) {
      setReportEdits((prev) => {
        const next = { ...prev }
        delete next[toggledEditControl.backendKey]
        return next
      })
    }

    setSelectedFindings((prev) => {
      if (findingId.includes('_hadlock_')) {
        const baseFindingId = findingId.split('_hadlock_')[0] + '_estimated_fetal_weight_' + findingId.split('_').slice(-1)[0]

        const filteredPrev = prev.filter(id => !id.includes('_hadlock_') || !id.startsWith(baseFindingId.split('_estimated_fetal_weight_')[0]))

        if (prev.includes(findingId)) {
          return filteredPrev.filter(id => id !== findingId)
        } else {
          return [...filteredPrev, findingId]
        }
      } else {
        return prev.includes(findingId) ? prev.filter((id) => id !== findingId) : [...prev, findingId]
      }
    })
  }

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category],
    )
  }

  const clearAllFilters = () => {
    setSelectedCategories([])
  }

  const handleSeriesChange = (seriesId: string) => {
    setActiveSeries(seriesId)
    setSelectedCategories([])
    setSelectedFindings([])
    setShowReportMode(false)
    setReportEdits({})
    setFindingSeriesIndex({})
  }

  const formatEditOptionLabel = (label: string) => {
    if (!label.includes(".")) {
      return label;
    }

    const translated = tReport(label);
    if (translated !== label) {
      return translated;
    }

    return label
      .replace(/^options\./, "")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (char) => char.toUpperCase());
  }

  const setReportEditForFinding = (
    finding: Finding,
    backendKey: string,
    value: string
  ) => {
    setReportEdits((prev) => ({
      ...prev,
      [backendKey]: value,
    }))
    setSelectedFindings((prev) =>
      prev.includes(finding.id) ? prev : [...prev, finding.id]
    )
  }

  const handleAddToReport = async () => {
    if (!canUpdateActiveSeries) {
      toast.warning(t("reportMode.selectSeries"))
      return
    }

    if (selectedFindings.length === 0 && reportEditCount === 0) {
      toast.warning(t("reportMode.selectFinding"))
      return
    }

    const selectedFindingDetails = getSelectedFindingDetails(
      selectedFindings,
      currentSeriesFindings
    );

    console.log("Adding to report:", selectedFindingDetails)

    const findingsData = buildReportFindingsPayload({
      studyInstanceUid,
      activeSeries,
      canUpdateActiveSeries,
      selectedFindingDetails,
      reportEdits,
    });

    try {
      console.log("Sending findings data to backend:", findingsData)
      await updateReportWithFindings(findingsData)

      setSelectedFindings([])
      setShowReportMode(false)
      setReportEdits({})

      refetchFindings()
    } catch (error) {
      console.error("Error updating study report with findings:", error)
      toast.error(t("reportMode.updateError"))
    }
  }

  const navigateFindingSeries = (finding: any, direction: 'prev' | 'next') => {
    const typeKey = `${finding.fieldKey}_${finding.category}`

    const allSeriesForFinding = findingsByType[typeKey] || []

    const filteredSeriesFindings = allSeriesForFinding.filter(f => {
      if (selectedCategories.length > 0) {
        return selectedCategories.includes(f.category)
      }
      return true
    })

    if (filteredSeriesFindings.length <= 1) return

    const currentIndex = findingSeriesIndex[typeKey] || 0

    let newIndex: number
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : filteredSeriesFindings.length - 1
    } else {
      newIndex = currentIndex < filteredSeriesFindings.length - 1 ? currentIndex + 1 : 0
    }

    setFindingSeriesIndex(prev => ({
      ...prev,
      [typeKey]: newIndex
    }))
  }

 

  if (isLoadingFindings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Activity className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-2 dark:text-gray-500" />
          <p className="text-slate-600 dark:text-gray-400">{t("loading")}</p>
        </div>
      </div>
    )
  }

  if (errorFindings) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4 mx-auto">
          <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
        </div>
        <h3 className="text-lg font-medium text-red-900 dark:text-red-400 mb-2">{t("error.title")}</h3>
        <p className="text-red-600 dark:text-red-400 text-sm max-w-md mx-auto">
          {t("error.description")}
        </p>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-card-color-200 dark:bg-gray-900">
      <FindingsToolbar
        activeSeries={activeSeries}
        selectedCategoriesCount={selectedCategories.length}
        seriesCount={series.length}
        showFilters={showFilters}
        t={t}
        visibleSeries={visibleSeries}
        onSeriesChange={handleSeriesChange}
        onToggleFilters={() => setShowFilters(!showFilters)}
      />

      {showFilters && (
        <FindingsFilters
          categories={categories}
          selectedCategories={selectedCategories}
          t={t}
          onClearAll={clearAllFilters}
          onToggleCategory={handleCategoryToggle}
        />
      )}

      <FindingsSummary
        canUpdateActiveSeries={canUpdateActiveSeries}
        currentCount={filteredFindings.length}
        isUpdatingReport={isUpdatingReport}
        reportEditCount={reportEditCount}
        selectedCount={selectedFindings.length}
        showReportMode={showReportMode}
        totalCount={currentSeriesFindings.length}
        t={t}
        onSubmitReport={handleAddToReport}
        onToggleReportMode={() => {
          setShowReportMode(!showReportMode)
          if (showReportMode) {
            setSelectedFindings([])
            setReportEdits({})
          }
        }}
      />

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {/* Unified Findings Grid */}
        {filteredFindings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-6">
            {filteredFindings.map((finding) => (
              <div key={finding.id} className="w-full">
                <FindingCard
                  finding={finding}
                  studyInstanceUid={studyInstanceUid}
                  activeSeries={activeSeries}
                  canUpdateActiveSeries={canUpdateActiveSeries}
                  findingSeriesIndex={findingSeriesIndex}
                  findingsByType={findingsByType}
                  formatEditOptionLabel={formatEditOptionLabel}
                  isSelected={selectedFindings.includes(finding.id)}
                  reportEdits={reportEdits}
                  selectedCategories={selectedCategories}
                  showReportMode={showReportMode}
                  onEditChange={setReportEditForFinding}
                  onNavigateSeries={navigateFindingSeries}
                  onToggle={handleFindingToggle}
                />
              </div>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4 mx-auto">
              <Activity className="w-8 h-8 text-slate-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">{t("empty.title")}</h3>
            <p className="text-slate-500 dark:text-gray-400 text-sm max-w-md mx-auto">
              {currentSeriesFindings.length === 0 ?
                activeSeries === "Report" ?
                  t("empty.reportEmpty") :
                  t("empty.studyEmpty") :
                t("empty.tryAdjustFilters")
              }
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default Findings 
